/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { Spin } from 'antd';

import { completeOidcAuthorizationRequestIfPossible, performOidcAuthorizationRequest } from '../../utils/oidc';
import { AUTHENTICATION_METHODS } from '../../constants/main';
import { INTEGRATION_TYPE, INTEGRATIONS } from '../../constants/integrations';
import { URL_REGEX } from '../../constants/regex';
import { useAuthenticateUserWithRedirects } from '../../utils/hooks/useAuthenticateUserWithRedirects';
import { useDispatch } from 'react-redux';
import { authActions } from '../../store/auth/authSlice';
import { useOidcRequest } from '../../utils/hooks/useOidcRequest';

const OidcAuthorizationCompletionLoader = ({
  organizationId,
  loginSettings,
  onAuthorizationCompletion
}) => {
  const dispatch = useDispatch();
  const [loginUser] = useAuthenticateUserWithRedirects();
  const { oidcVariables } = useOidcRequest();

  useEffect(() => {
    if (!loginSettings) {
      return;
    }

    dispatch(authActions.updateIsOidcAuthorizationCompletionInProgress(true));
    completeOidcAuthorizationRequestIfPossible(async (body) => {
      const oidcIntegration = INTEGRATIONS.find(integration => integration.type === INTEGRATION_TYPE.OIDC_AUTHENTICATION);
      const authenticationPlugins = loginSettings?.plugins?.filter(plugin => plugin.category === oidcIntegration?.pluginCategory) || [];
      const shouldAutoLogin = loginSettings?.isSkipNativeLoginEnabled && authenticationPlugins.length === 1;
      if (body) {
        const response = await loginUser({
          authenticationMethod: AUTHENTICATION_METHODS.INTEGRATION,
          data: body,
          loginSettings
        });

        const isUrl = URL_REGEX.test(loginSettings?.failedAuthenticationRedirectUrl);
        if (response.error && shouldAutoLogin && isUrl) {
          const url = loginSettings.failedAuthenticationRedirectUrl.trim();
          const paramCon = url.includes('?') ? '&' : '?';
          window.location.href = `${url}${paramCon}message=${encodeURIComponent(response.error.details)}&code=${encodeURIComponent(response.error.code)}&status=${encodeURIComponent(response.error.status)}`;
          return;
        }

        dispatch(authActions.updateIsOidcAuthorizationCompletionInProgress(false));
        onAuthorizationCompletion?.(body);
      } else if (shouldAutoLogin) {
        const integration = authenticationPlugins?.[0]?.integration;
        performOidcAuthorizationRequest({ integration, organizationId, variables: oidcVariables });
      } else {
        dispatch(authActions.updateIsOidcAuthorizationCompletionInProgress(false));
        onAuthorizationCompletion?.(null);
      }
    });
  }, [loginSettings]);

  return (
    <div className={'w-100 h-100vh d-flex justify-content-center align-items-center'}>
      <Spin />
    </div>
  );
};

OidcAuthorizationCompletionLoader.propTypes = {
  organizationId: PropTypes.string,
  loginSettings: PropTypes.object,
  onAuthorizationCompletion: PropTypes.func.isRequired
};

export default OidcAuthorizationCompletionLoader;
